import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useLocationScope } from "@/context/LocationContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Download, DollarSign, Receipt, Landmark } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

const currentMonth = () => new Date().toISOString().slice(0, 7);

export default function Reports() {
  const { selectedLot, isAll } = useLocationScope();
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { month };
      if (!isAll && selectedLot) params.lotId = selectedLot;
      const { data } = await api.get("/reports/revenue", { params });
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [month, isAll, selectedLot]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    if (!data) return;
    const headers = ["Date", "Ticket #", "Lot", "Type", "Method", "Base", "Sales Tax", "Total"];
    const rows = data.transactions.map((t) => [
      t.date, t.ticketNumber, t.lotName, t.kind, t.method,
      t.base.toFixed(2), t.tax.toFixed(2), t.total.toFixed(2),
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c ?? ""}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `revenue-${data.month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const s = data?.summary;

  return (
    <div className="px-4 py-6 md:px-8">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-slate-50">Revenue Report</h1>
          <p className="text-slate-400">Monthly revenue &amp; sales tax collected {isAll ? "across all lots" : "for this location"}.</p>
        </div>
        <div className="flex items-end gap-3">
          <div className="space-y-1">
            <label className="text-xs text-slate-400">Month</label>
            <Input
              data-testid="reports-month"
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="h-11 w-44 border-slate-700 bg-slate-900 text-slate-100"
            />
          </div>
          <Button
            data-testid="report-export"
            onClick={exportCsv}
            disabled={!data || !data.transactions.length}
            className="h-11 gap-2 bg-[#A0148C] font-bold hover:bg-[#80106F]"
          >
            <Download className="h-4 w-4" /> Export CSV
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex h-[50vh] items-center justify-center">
          <Loader2 className="h-10 w-10 animate-spin text-[#A0148C]" />
        </div>
      ) : !data ? (
        <Card data-testid="report-error" className="border-slate-800 bg-slate-900 p-10 text-center text-slate-400">
          Couldn't load the report. Please try again.
          <div className="mt-4">
            <Button onClick={load} className="bg-[#A0148C] font-bold hover:bg-[#80106F]">Retry</Button>
          </div>
        </Card>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Kpi testid="report-total" icon={DollarSign} label="Total Collected" value={s.total} accent />
            <Kpi testid="report-base" icon={Receipt} label="Base Revenue" value={s.base} />
            <Kpi testid="report-tax" icon={Landmark} label="Sales Tax Collected" value={s.tax} />
          </div>

          <div className="flex flex-wrap gap-2 text-sm">
            {Object.entries(data.byKind).map(([kind, v]) => (
              <span key={kind} className="rounded-full border border-slate-700 bg-slate-900 px-3 py-1 text-slate-300">
                <span className="font-semibold capitalize text-slate-100">{kind}</span> · {v.count} txns · ${v.total.toFixed(2)}
              </span>
            ))}
            {!Object.keys(data.byKind).length && <span className="text-slate-500">No transactions this month.</span>}
          </div>

          {data.byDay.length > 0 && (
            <Card className="border-slate-800 bg-slate-900 p-5">
              <h2 className="mb-4 font-heading text-lg font-bold text-slate-100">Daily revenue</h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.byDay}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis dataKey="date" tick={{ fill: "#94a3b8", fontSize: 11 }} tickFormatter={(d) => d.slice(8)} />
                    <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} />
                    <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", color: "#e2e8f0" }} />
                    <Bar dataKey="total" name="Revenue ($)" fill="#A0148C" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          )}

          <Card className="border-slate-800 bg-slate-900">
            <div className="border-b border-slate-800 px-5 py-4">
              <h2 className="font-heading text-lg font-bold text-slate-100">By location</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-5 py-3">Location</th>
                    <th className="px-5 py-3 text-right">Txns</th>
                    <th className="px-5 py-3 text-right">Base</th>
                    <th className="px-5 py-3 text-right">Sales Tax</th>
                    <th className="px-5 py-3 text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800 font-data">
                  {data.byLot.map((l) => (
                    <tr key={l.lotId} data-testid={`report-lot-row-${l.lotId}`} className="text-slate-300">
                      <td className="px-5 py-3 font-sans font-semibold text-slate-100">{l.lotName}</td>
                      <td className="px-5 py-3 text-right">{l.count}</td>
                      <td className="px-5 py-3 text-right">${l.base.toFixed(2)}</td>
                      <td className="px-5 py-3 text-right text-[#d946ef]">${l.tax.toFixed(2)}</td>
                      <td className="px-5 py-3 text-right font-bold text-slate-100">${l.total.toFixed(2)}</td>
                    </tr>
                  ))}
                  {!data.byLot.length && (
                    <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-500">No revenue recorded for {data.month}.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent, testid }) {
  return (
    <Card className={`border-slate-800 p-5 ${accent ? "bg-[#A0148C]/10" : "bg-slate-900"}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
        <Icon className={`h-5 w-5 ${accent ? "text-[#A0148C]" : "text-slate-500"}`} />
      </div>
      <p data-testid={testid} className="mt-2 font-data text-3xl font-black text-slate-50">
        ${(value ?? 0).toFixed(2)}
      </p>
    </Card>
  );
}
